import { VerificationEntityEditConfirmationModal } from "#components";
import useVerificationsDatabase from "./supabase/useVerificationsDatabase";

export const CONFIRM_VERIFICATION_INVALIDATION_FIELD =
  "confirmVerificationInvalidation";

const VERIFICATION_EDIT_CONFIRMATION_ERROR_CODES = [
  "ACTIVE_VERIFICATION_IN_PROGRESS",
  "VERIFIED_ENTITY_EDIT_CONFIRMATION_REQUIRED",
];

export function useVerificationEntityEditGuard() {
  async function getConfirmationState(entityId: string, isVerified: boolean) {
    const activeVerification = await useVerificationsDatabase().getActiveVerification(
      entityId,
    );

    return {
      hasActiveVerification: Boolean(activeVerification),
      isVerified,
      requiresConfirmation: Boolean(activeVerification) || isVerified,
    };
  }

  function requestConfirmation(params: {
    hasActiveVerification: boolean;
    isVerified: boolean;
  }): Promise<boolean> {
    return new Promise((resolve) => {
      useModal().open(VerificationEntityEditConfirmationModal, {
        hasActiveVerification: params.hasActiveVerification,
        isVerified: params.isVerified,
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  }

  function isVerificationEditConfirmationError(error: any) {
    const confirmationState = getConfirmationStateFromError(error);

    return (
      confirmationState.hasActiveVerification || confirmationState.isVerified
    );
  }

  function getConfirmationStateFromError(
    error: any,
    fallback?: { hasActiveVerification?: boolean; isVerified?: boolean },
  ) {
    const serializedError = stringifyError(error);
    const statusMessage = String(
      error?.data?.statusMessage ??
        error?.data?.error?.statusMessage ??
        error?.statusMessage ??
        error?.message ??
        error?.value?.data?.statusMessage ??
        error?.value?.data?.error?.statusMessage ??
        error?.value?.statusMessage ??
        serializedError,
    );

    const code = String(
      error?.data?.data?.code ??
        error?.data?.error?.data?.code ??
        error?.data?.code ??
        error?.value?.data?.data?.code ??
        error?.value?.data?.error?.data?.code ??
        error?.value?.data?.code ??
        "",
    );

    const hasActiveVerification =
      Boolean(fallback?.hasActiveVerification) ||
      statusMessage.includes("ACTIVE_VERIFICATION_IN_PROGRESS") ||
      code === "ACTIVE_VERIFICATION_IN_PROGRESS" ||
      Boolean(
        error?.data?.data?.hasActiveVerification ??
          error?.data?.error?.data?.hasActiveVerification ??
          error?.data?.hasActiveVerification ??
          error?.value?.data?.data?.hasActiveVerification ??
          error?.value?.data?.error?.data?.hasActiveVerification ??
          error?.value?.data?.hasActiveVerification,
      );

    const isVerified =
      Boolean(fallback?.isVerified) ||
      statusMessage.includes("VERIFIED_ENTITY_EDIT_CONFIRMATION_REQUIRED") ||
      code === "VERIFIED_ENTITY_EDIT_CONFIRMATION_REQUIRED" ||
      Boolean(
        error?.data?.data?.isVerified ??
          error?.data?.error?.data?.isVerified ??
          error?.data?.isVerified ??
          error?.value?.data?.data?.isVerified ??
          error?.value?.data?.error?.data?.isVerified ??
          error?.value?.data?.isVerified,
      );

    return {
      hasActiveVerification,
      isVerified,
      requiresConfirmation: VERIFICATION_EDIT_CONFIRMATION_ERROR_CODES.some(
        (confirmationCode) =>
          statusMessage.includes(confirmationCode) || code === confirmationCode,
      ),
    };
  }

  return {
    getConfirmationState,
    requestConfirmation,
    isVerificationEditConfirmationError,
    getConfirmationStateFromError,
  };
}

function stringifyError(error: unknown) {
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}
